import {
  View,
  StyleSheet,
  Pressable,
  Modal,
  ScrollView,
  Image,
} from "react-native";
import React, { memo, useEffect, useState } from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import CloseIcon from "@assets/svg/CloseIcon";
import color from "@constants/color";
import Text from "@components/CustomText";
import { useDispatch, useSelector } from "react-redux";
import { actions as authActions } from "@reducers/authReducer";
import messaging from "@react-native-firebase/messaging";
import t from "@constants/localization";
import analytics from "@services/analytics";
import { LOGOUT_INFO_EVENTS } from "@constants/analyticsEventsCT";
import {
  confirmLeader,
  getLoggedInDevices,
  isDeviceActive,
  logoutLeader,
} from "@services/logout";
import * as Sentry from "@sentry/react-native";
import { actions as snackbarActions } from "@reducers/snackbarReducer";
import { useRoute } from "@react-navigation/native";
import { actions as delayedActions } from "@reducers/delayDataReducer";
import Button from "./Button";

const PATH = "screens/LoggedInDevices/InAppLogoutModal";

const loggedInModalSelector = (state) => state?.auth?.loggedInModal;
const LoggedInInfo = ({ navigation }) => {
  const insets = useSafeAreaInsets();
  const { visible = false } = useSelector(loggedInModalSelector);
  const activeDevicesInfo = useSelector(
    (state) => state.delayedDataReducer?.activeDevicesInfo,
  );

  useEffect(() => {
    if (!activeDevicesInfo) return;
    const { type, devices } = activeDevicesInfo;

    if (type !== "MODAL") return;
    if (Array.isArray(devices) && devices.length > 0) {
      const device = devices[0];
      const deviceId = device.device_id;
      (async () => {
        try {
          const { data: isActiveData } = await isDeviceActive(deviceId);
          const isCurrentAutoVerified =
            isActiveData.is_current_device_auto_verified;
          updateModalDataInState({ device, isCurrentAutoVerified });
        } catch (e) {
          updateModalDataInState({ device, isCurrentAutoVerified: false });
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDevicesInfo]);

  const [deviceInfo, setDeviceInfo] = useState({
    device: "",
    os: "",
    logged_in_at: "",
    device_id: null,
    isCurrentAutoVerified: null,
  });

  const dispatch = useDispatch();

  const { params: { pnData } = {} } = useRoute();

  const handleMessage = async (payload = {}) => {
    if (payload.type !== "NOTIFY_LOGGEDIN_DEVICES") {
      return;
    }
    const extraData =
      typeof payload.extraData === "string"
        ? JSON.parse(payload.extraData || {})
        : payload.extraData;
    const deviceId = extraData?.device_id;
    let isCurrentAutoVerified = false;

    if (!deviceId) {
      return;
    }

    try {
      const { data: isActiveData } = await isDeviceActive(deviceId);
      isCurrentAutoVerified = isActiveData.is_current_device_auto_verified;
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      updateModalDataInState({
        device: extraData,
        isCurrentAutoVerified,
      });
    }
  };

  const updateModalDataInState = ({ device, isCurrentAutoVerified }) => {
    if (!device.device_id) return;
    setDeviceInfo({ ...device, isCurrentAutoVerified });
    dispatch(
      authActions.setLoggedInModalData({
        visible: true,
      }),
    );
    analytics.trackEvent(LOGOUT_INFO_EVENTS.VIEWED_LOGIN_MODULE, {});
  };

  useEffect(() => {
    if (!pnData) return;
    const parsedPnData = JSON.parse(pnData);
    handleMessage(parsedPnData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pnData]);

  useEffect(() => {
    const unsubscribe = messaging().onMessage(async (remoteMessage = {}) => {
      const { data = {} } = remoteMessage;
      handleMessage(data);
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const handleModal = () => {
    dispatch(
      authActions.setLoggedInModalData({
        visible: false,
      }),
    );
  };

  const itsMe = () => {
    confirmLeader(deviceInfo?.device_id)
      .then(() => {
        dispatch(delayedActions.confirmDevice());
      })
      .catch((e) => Sentry.captureException(e))
      .finally((_) => {
        handleModal();
        setDeviceInfo({
          device: "",
          os: "",
          logged_in_at: "",
          device_id: null,
          isCurrentAutoVerified: null,
        });
      });
  };

  const logout = () => {
    dispatch(
      authActions.setLoggedInModalData({
        visible: false,
      }),
    );
    if (!deviceInfo.device_id) {
      navigation.navigate("LoggedInDevices", {});
    } else if (deviceInfo.isCurrentAutoVerified) {
      navigation &&
        navigation.navigate("LoggedInDevices", {
          fromModal: true,
          deviceId: deviceInfo.device_id,
        });
    } else {
      const id = deviceInfo.device_id;
      logoutLeader(id, "MODAL")
        .then((res) => {
          if (res?.data?.toastMessage) {
            showSnackbar(res.data.toastMessage);
          }
          analytics.trackEvent(LOGOUT_INFO_EVENTS.SUBMIT_LOGOUT_BUTTON, {});
        })
        .catch((e) => console.log(e))
        .finally(() => {
          fetchLoggedInData();
        });
    }
  };

  const fetchLoggedInData = () => {
    getLoggedInDevices()
      .then(() => {})
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {});
  };

  const showSnackbar = (msg = "") => {
    const snackbarPayload = {
      message: msg,
      autoHide: true,
      actionText: "Okay",
      actionHandler: (_) => {
        dispatch(snackbarActions.hideSnackbar());
      },
    };
    dispatch(snackbarActions.showSnackbar(snackbarPayload));
  };

  return (
    <Modal
      statusBarTranslucent
      navigationBarTranslucent
      visible={visible}
      transparent
      style={styles.container}
      animationType="slide"
    >
      <View style={styles.modalContainer}>
        <ScrollView
          style={styles.listContainer}
          contentContainerStyle={styles.contentContainerStyle}
        >
          <View style={styles.infoContainer}>
            <Pressable
              onPress={handleModal}
              style={styles.crossButton}
              testID="login_modal_close_button"
            >
              <CloseIcon />
            </Pressable>
            <View style={styles.bodyContainer}>
              <Image
                source={{
                  uri: "https://public-cdn.citymall.live/cm-admin-storage-public/cl_app_icon%2F1686052879546-DeviceLoginWarningIcon.png",
                }}
                resizeMode="contain"
                style={styles.warningIcon}
              />
              <Text variant="title_18" style={styles.title}>
                {t(`${PATH}/secondPhoneTitle`)}
              </Text>
              <Text variant="caption_2" style={styles.description}>
                {t(`${PATH}/waringInfo`)}
              </Text>
            </View>
            <View style={styles.deviceDetails}>
              <Text variant="body_text_2">{deviceInfo?.device}</Text>
              <Text
                style={styles.modalInfo}
              >{`${deviceInfo?.os}, ${deviceInfo?.logged_in_at}`}</Text>
            </View>
            <View style={[styles.footer, { paddingBottom: insets.bottom }]}>
              <Button
                onPress={logout}
                btnText={t(`${PATH}/logoutCTA`)}
                buttonStyle={styles.buttonContainer}
              />
              <Button
                btnText={t(`${PATH}/iAmThereCTA`)}
                buttonColor="white"
                borderColor={color.blank_grey}
                textColor="black"
                buttonStyle={styles.buttonContainer}
                onPress={itsMe}
              />
            </View>
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  listContainer: {
    maxHeight: "90%",
  },
  modalContainer: {
    backgroundColor: color.modalBackground,
    flexDirection: "column",
    justifyContent: "flex-end",
    flex: 1,
  },
  contentContainerStyle: {
    flexDirection: "column",
    justifyContent: "flex-end",
    flexGrow: 1,
  },
  infoContainer: {
    backgroundColor: "#fff",
    width: "100%",
    alignItems: "center",
    padding: 16,
    borderTopEndRadius: 20,
    borderTopStartRadius: 20,
  },
  crossButton: {
    alignSelf: "flex-end",
    width: 40,
    height: 40,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 100,
  },
  bodyContainer: {
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    textAlign: "center",
    marginTop: 8,
  },
  description: {
    color: color.lightBlack,
    textAlign: "center",
    margin: 8,
  },
  deviceDetails: {
    borderColor: color.lighterBlack,
    borderWidth: 1,
    width: "100%",
    padding: 16,
    borderRadius: 10,
  },
  modalInfo: {
    color: color.lightBlack,
  },
  footer: {
    flexDirection: "row",
    paddingTop: 12,
    width: "100%",
    justifyContent: "space-between",
  },
  buttonContainer: {
    borderRadius: 10,
  },
  warningIcon: {
    width: 100,
    height: 100,
  },
});

export default memo(LoggedInInfo);
